import { StrictMode, useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { I18nProvider } from '@lingui/react';
import { Provider, useAtomValue } from 'jotai';
import * as Sentry from '@sentry/react';
import axios from 'axios';
import { i18n, activateInitialLocale } from './i18n';
import { localeAtom } from './lib/atoms';
import { cameraMode } from './lib/cameraMode';
import { ErrorBoundary } from './components/ErrorBoundary';
import App from './App';
import api from './lib/api';
import './styles/globals.css';

// A deploy deletes hashed chunks the open tab still imports, and Pages serves
// index.html for that URL. Reload once onto the new document. A second failure
// inside this window is left to surface.
const CHUNK_RELOAD_AT = 'chunk-reload-at';
const CHUNK_RELOAD_WINDOW_MS = 10_000;

window.addEventListener('vite:preloadError', (event) => {
  const reloadedAt = Number(sessionStorage.getItem(CHUNK_RELOAD_AT) || 0);
  if (Date.now() - reloadedAt < CHUNK_RELOAD_WINDOW_MS) return;

  sessionStorage.setItem(CHUNK_RELOAD_AT, String(Date.now()));
  event.preventDefault();
  window.location.reload();
});

// Read now: a ?camera= override is kept for the tab only if seen before step navigation drops it.
const initialCameraMode = cameraMode();

// Initialize Sentry if DSN is provided
if (import.meta.env.VITE_SENTRY_DSN) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,

    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        maskAllInputs: true,
        blockAllMedia: true,
      }),
    ],

    enableLogs: true,

    // Performance monitoring sample rate (100% in development, 10% in production)
    tracesSampleRate: import.meta.env.MODE === 'production' ? 0.1 : 1.0,
    // Replay only when we report an error. Ordinary visits are not recorded.
    // The native camera app is outside the page, so this shows our screens only.
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1,

    beforeSend(event, hint) {
      const error = hint.originalException;
      if (axios.isAxiosError(error) && error.response?.status === 400) return null;
      return event;
    },
  });
  // Compare "capture lost on page reload" between the in-page and native camera.
  Sentry.setTag('camera_mode', initialCameraMode);
}

function I18nWrapper({ children }: { children: React.ReactNode }) {
  const locale = useAtomValue(localeAtom);
  return (
    <I18nProvider i18n={i18n} key={locale}>
      {children}
    </I18nProvider>
  );
}

function ApiLocaleSync() {
  const locale = useAtomValue(localeAtom);

  // Set Accept-Language header whenever locale changes
  useEffect(() => {
    api.defaults.headers.common['Accept-Language'] = locale;
  }, [locale]);

  return null;
}

const root = createRoot(document.getElementById('root')!);

// Render only once the ?lang= catalog is active; on failure fall back to English.
activateInitialLocale()
  .catch((err) => Sentry.captureException(err))
  .finally(() => {
    root.render(
      <StrictMode>
        <Provider>
          <ApiLocaleSync />
          <I18nWrapper>
            <ErrorBoundary>
              <BrowserRouter>
                <App />
              </BrowserRouter>
            </ErrorBoundary>
          </I18nWrapper>
        </Provider>
      </StrictMode>
    );
  });
